import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Box, Flex, HStack, IconButton, Stack, VStack } from '@chakra-ui/react';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Deal, DealStateEnum, estimateUpsert } from '../../gql/dealGql';
import { DealStates } from '../../gql/dealStatesGql';
import {
  DealType,
  PaperworkType,
  useOnSecureLinkCreatedSubscription,
  useOnSecureLinkExpiredSubscription,
} from '../../gql/generated/graphql';

import CommunicationHistory from '../../components/CommunicationHistory';
import LeaseEndContainer from '../../components/Container/LEContainer';
import CreditApplication from '../../components/CreditApplication/CreditApplication';
import CustomerInfo from '../../components/CustomerInfo';
import CustomerInfoAcquisition from '../../components/CustomerInfoAcquisition/CustomerInfoAcquisition';
import DealInfo from '../../components/DealInfo/DealInfo';
import DealInfoAcquisition from '../../components/DealInfoAcquisition/DealInfoAcquisition';
import DealInfoBuyout from '../../components/DealInfoBuyout/DealInfoBuyout';
import DealIssuesModal from '../../components/DealIssuesModal';
import EstimateForm from '../../components/EstimateForm/EstimateForm';
import MediaCenter from '../../components/MediaCenter';
import PayoffInfo from '../../components/PayoffInfo/PayoffInfo';
import RequestBoot from '../../components/RequestBoot/RequestBoot';
import TagInfo from '../../components/TagInfo/TagInfo';
import { BootActionsWithContext } from '../../components/Task/TaskActions/BootAction';
import ReviveBootAction from '../../components/Task/TaskActions/ReviveBootAction';
import UnwindDeal from '../../components/UnwindDeal/UnwindDeal';
import UnwindDealToApproved, {
  stateIsUnwindableToApproved,
} from '../../components/UnwindDeal/UnwindDealToApproved';
import VehicleInfo from '../../components/VehicleInfo/VehicleInfo';
import Card from '../../components/shared/Card';
import CardHeaderV2 from '../../components/shared/Card/components/CardHeaderV2';

import { BootBox, ChangeDealStateSelect } from './ChangeDealStateSelect';
import AdobeContractStatusTimeline from './ContractStatusTimeline/AdobeContractStatusTimeline';
import ContractStatusTimeline from './ContractStatusTimeline/ContractStatusTimeline';
import DealLockedModal from './DealLockedModal';
import DealStatesModal from './DealStatesModal';
import DuplicateDealModalButton from './DuplicateDealModalButton';
import LockedDealOverlay from './LockedDeal/LockedDealOverlay';
import BackButton from './ProgressionButtons/BackButton';
import ClaimButton from './ProgressionButtons/ClaimButton';
import NotificationSubscribeButton from './ProgressionButtons/NotificationSubscribeButton';
import OverrideSeenDealButton from './ProgressionButtons/OverrideSeenDealButton';

import { isDealInOrPastFunded } from '../../constants/dealStates';
import { LDFlags } from '../../constants/experiments';
import { PermissionEnum } from '../../constants/permissions';
import ROUTES from '../../constants/routes';
import useFlag from '../../hooks/useFlag';
import useGenerateBookSheets from '../../hooks/useGenerateBookSheets';
import { useLienholder } from '../../hooks/useLienholder';
import usePageTitle from '../../hooks/usePageTitle';
import { useUser } from '../../hooks/useUser';
import { Can } from '../../libs/Can';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import { AbilityContext } from '../../libs/contextLib';
import Notes from '../../providers/Notes';
import { getIsSecureLinkActive } from '../../utils/customers';
import { cleanDealForEstimateUpsert } from '../../utils/deals';
import { isUserAssignedTitleTeamLead, isUserAssignedToDeal } from '../../utils/user';

const determineCenterBoxByPreviousState = (
  previousState: string | undefined,
  deal: Deal,
  onSaveEstimate: (estimate: Deal, setSubmitting: (isSubmitting: boolean) => void) => void,
) => {
  if (previousState === DealStateEnum.Estimate) {
    return deal.customer?.first_name ? (
      <Card variant="roundedWithBorder">
        <CardHeaderV2 title="Edit Estimate" showPodColor pod={deal.pod}>
          <DealStatesModal />
        </CardHeaderV2>
        <Box>
          <EstimateForm estimate={deal} onSave={onSaveEstimate} />
        </Box>
      </Card>
    ) : null;
  }

  if (previousState === DealStateEnum.SoftClose) {
    return <CreditApplication />;
  }

  return <DealInfoBuyout />;
};

const DealDetail = () => {
  const user = useUser();
  const secureLinkFlag = useFlag(LDFlags.SECURE_INFO_LINK);

  const {
    deal,
    dispatch,
    dealRefetch,
    isLoading,
    jurisdictionData: { jurisdiction },
    setUserChangedSomething,
  } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  usePageTitle({ deal });

  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const customerIds = [deal.customer?.id, deal.cobuyer?.id].filter(
    (customerId) => customerId != null,
  );
  const skipSecureLinkSubscriptions = !secureLinkFlag || customerIds.length === 0;
  useOnSecureLinkCreatedSubscription({
    skip: skipSecureLinkSubscriptions,
    variables: {
      customerIds,
    },
    onData: ({ data: { data } }) => {
      const notification = data?.onSecureLinkCreated;
      if (!notification) {
        return;
      }

      // Block autosave.
      setUserChangedSomething(false);
      dealRefetch();
    },
  });
  useOnSecureLinkExpiredSubscription({
    skip: skipSecureLinkSubscriptions,
    variables: {
      customerIds,
    },
    onData: ({ data: { data } }) => {
      const notification = data?.onSecureLinkExpired;
      if (!notification) {
        return;
      }

      const personKey = notification.customer_id === deal.customer?.id ? 'customer' : 'cobuyer';
      dispatch({
        type: DealActionsEnum.DeepUpdateDeal,
        payload: { [personKey]: { secure_link: null } },
      });
    },
  });

  useEffect(() => {
    const urlIdParamChanged = deal.id && deal.id.toString() !== id;
    const forceReload = () => history.go(0);

    if (urlIdParamChanged) {
      forceReload();
    }
  }, [deal.id, id, history]);

  const [upsertEstimate] = useMutation<{ estimateUpsert: Deal }>(estimateUpsert);
  const { generateBookSheets } = useGenerateBookSheets();

  const { doubleTax, directPayState, residualValue } = useLienholder({ data: deal });
  const [showIssuesIfPresent, setShowIssuesIfPresent] = useState(true);

  const { canEditNotes, canPinNotes } = useMemo(
    () => ({
      canEditNotes: abilities.has(PermissionEnum.EditNotes),
      canPinNotes: abilities.has(PermissionEnum.PinNotes),
    }),
    [abilities],
  );
  const { isAssignedToDeal, isAssignedTitleTeamLead } = useMemo(
    () => ({
      isAssignedToDeal: isUserAssignedToDeal(user, deal),
      isAssignedTitleTeamLead: isUserAssignedTitleTeamLead(user, deal),
    }),
    [user, deal],
  );

  const canManagePins = useMemo(
    () => canPinNotes || isAssignedToDeal || isAssignedTitleTeamLead,
    [canPinNotes, isAssignedToDeal, isAssignedTitleTeamLead],
  );

  const canEditNote = useMemo(
    () => canEditNotes || isAssignedTitleTeamLead,
    [canEditNotes, isAssignedTitleTeamLead],
  );

  const onSaveEstimate = useCallback(
    async (estimate: Deal, setSubmitting: (isSubmitting: boolean) => void) => {
      const updatedDeal = cleanDealForEstimateUpsert({
        ...estimate,
        state: estimate.state,
      });

      try {
        const res = await upsertEstimate({
          variables: {
            deal: {
              ...updatedDeal,
              car: {
                ...updatedDeal.car,
                registration_expiration: updatedDeal.car?.registration_expiration || null,
              },
              referral_source: updatedDeal.referral_source?.source_name
                ? updatedDeal.referral_source
                : null,
            },
          },
        });

        if (!res.data?.estimateUpsert) {
          throw new Error('No data returned');
        }

        const { estimateUpsert: dealFromAPI } = res.data;
        const { id: dealId, state } = dealFromAPI;

        if (state === DealStateEnum.SoftClose || state === DealStateEnum.Floor) {
          await generateBookSheets({
            dealId,
            customerState: deal.customer.address?.state,
            country: deal.country,
          });
        }

        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: dealFromAPI as Partial<Deal>,
        });

        if (state === DealStateEnum.Floor) {
          history.push(ROUTES.DASHBOARD);
        }
      } catch (e) {
        const error = e as Error;
        logger.error('DealDetail.tsx', 'onSaveEstimate', null, error);
        toast.error(
          `Error saving Estimate: ${error.message || error}. Please refresh and try again.`,
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      history.push,
      dispatch,
      generateBookSheets,
      upsertEstimate,
      deal.customer.address?.state,
      deal.country,
    ],
  );

  const renderCenterBox = () => {
    switch (deal.state) {
      case DealStateEnum.Estimate: {
        return deal.customer?.first_name ? (
          <Card variant="roundedWithBorder" mb={3}>
            <CardHeaderV2 title="Edit Estimate" showPodColor pod={deal.pod}>
              <DealStatesModal />
            </CardHeaderV2>
            <Box>
              <EstimateForm estimate={deal} onSave={onSaveEstimate} />
            </Box>
          </Card>
        ) : null;
      }
      case DealStateEnum.SoftClose: {
        return <CreditApplication />;
      }
      case DealStateEnum.Booted: {
        const dealStates = deal.deal_states;
        dealStates.sort((a: DealStates, b: DealStates) => {
          const aDate = a?.updated_date_utc || '';
          const bDate = b?.updated_date_utc || '';
          if (new Date(aDate) > new Date(bDate)) {
            return -1;
          }
          if (new Date(aDate) < new Date(bDate)) {
            return 1;
          }
          return 0;
        });
        const previousDealState = dealStates.find(
          (ds) => [DealStateEnum.Booted].indexOf(ds.state) === -1,
        );
        return determineCenterBoxByPreviousState(previousDealState?.state, deal, onSaveEstimate);
      }
      default: {
        return <DealInfoBuyout />;
      }
    }
  };

  const renderContractStatusTimeline = () => {
    const shouldRenderSomeContractTimeline =
      deal.type !== DealType.Acquisition &&
      deal.paperwork_type !== PaperworkType.Paper &&
      [DealStateEnum.Closed, DealStateEnum.SentForSignatures, DealStateEnum.Signed].includes(
        deal.state,
      );
    if (!shouldRenderSomeContractTimeline) {
      return null;
    }

    if (deal.paperwork_type === PaperworkType.Adobe) {
      return <AdobeContractStatusTimeline />;
    }

    // For signing on both R1 and Lease End
    return <ContractStatusTimeline />;
  };

  return (
    <Box>
      <Flex bgColor="queenBlue" px={{ base: 1, sm: 5 }} py={2} wrap={{ base: 'unset', sm: 'wrap' }}>
        <HStack justify={{ base: 'flex-start', sm: 'start' }} flex={1}>
          <IconButton
            aria-label="Return"
            icon={<MdKeyboardArrowLeft size="30px" />}
            isRound
            onClick={() => history.goBack()}
          />

          <BackButton />

          {stateIsUnwindableToApproved(deal.state, deal.approved_date) ? (
            <UnwindDealToApproved deal={deal} refetch={dealRefetch} />
          ) : null}

          {isDealInOrPastFunded(deal.state) ? (
            <UnwindDeal deal={deal} refetch={dealRefetch} />
          ) : null}
        </HStack>

        <Stack
          align="center"
          direction={{ base: 'column', sm: 'row' }}
          flex={1}
          justify="center"
          px={{ base: 1, sm: 'unset' }}
        >
          <ChangeDealStateSelect />
          {deal.request_boot && <BootBox text="Boot Requested" />}
        </Stack>

        <HStack justify={{ base: 'flex-end', md: 'end' }} flex={1}>
          <Can I={PermissionEnum.OverrideCalledByFS}>
            <OverrideSeenDealButton />
          </Can>
          {abilities.has(PermissionEnum.ClaimDealAsAdvisor) ||
          abilities.has(PermissionEnum.ClaimBootRequest) ? (
            <ClaimButton />
          ) : null}
          <DuplicateDealModalButton />
          <Can I={PermissionEnum.SubscribeToNotifications}>
            <NotificationSubscribeButton />
          </Can>
          <Can I={PermissionEnum.BootDeal}>
            <BootActionsWithContext />
          </Can>
          <ReviveBootAction />
          <Can I={PermissionEnum.RequestBoot}>
            <RequestBoot />
          </Can>
        </HStack>
      </Flex>
      <LeaseEndContainer isLoading={isLoading}>
        <Notes>
          <Box>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={2}
              justify="center"
              maxW="2000px"
              overflowX="auto"
              mx="auto"
            >
              {/* Customer/Payoff Info */}
              <VStack w={{ base: '100%', md: '25%' }}>
                {deal.type === DealType.Buyout ||
                deal.type === DealType.Refi ||
                deal.type === DealType.Marketplace ? (
                  <CustomerInfo />
                ) : (
                  <CustomerInfoAcquisition />
                )}
                <PayoffInfo />
              </VStack>
              {/* Deal Details */}
              <VStack w={{ base: '100%', md: '50%' }}>
                {deal.type !== DealType.Acquisition || deal.state === DealStateEnum.Estimate ? (
                  renderCenterBox()
                ) : (
                  <Card minH="863px">
                    <CardHeaderV2 title="Deal Info" showPodColor pod={deal.pod}>
                      <DealStatesModal />
                    </CardHeaderV2>
                    <Box>
                      <DealInfoAcquisition />
                    </Box>
                  </Card>
                )}
                {renderContractStatusTimeline()}
              </VStack>
              {/* Deal/Vehicle Info & Tags */}
              <VStack w={{ base: '100%', md: '25%' }}>
                <DealInfo />
                <VehicleInfo />
                <TagInfo />
              </VStack>
            </Stack>
            {/* Notes & Media Center */}
            <Flex
              mx="auto"
              mt={4}
              gap={2}
              flexDirection={{ base: 'column', md: 'row' }}
              maxW="2000px"
            >
              <Box w={{ base: '100%', md: '50%' }}>
                <CommunicationHistory
                  canManagePins={canManagePins}
                  canEditNote={canEditNote}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  dealId={deal.id!}
                />
              </Box>
              <Box w={{ base: '100%', md: '50%' }}>
                <MediaCenter />
              </Box>
            </Flex>
          </Box>
        </Notes>
      </LeaseEndContainer>

      <DealIssuesModal
        isOpen={showIssuesIfPresent}
        onAcknowledge={() => setShowIssuesIfPresent(false)}
        onDismiss={() => setShowIssuesIfPresent(false)}
        deal={deal}
        directPayState={directPayState}
        doubleTax={doubleTax}
        requiresWalkIn={jurisdiction?.requireWalkIn ?? false}
        residualValue={residualValue}
      />

      <LockedDealOverlay lockedReason={deal.lock_status ?? null} />
      <DealLockedModal
        isOpen={getIsSecureLinkActive(secureLinkFlag, deal.customer)}
        expirationDate={deal.customer?.secure_link?.expiration_date}
      />
    </Box>
  );
};

export default DealDetail;
